/**
 * Placement arithmetic the agent should never have to do itself: which wall is north, where a
 * run of cabinets lands along a wall, where a nightstand sits beside a bed, where chairs go
 * around a table. Pure functions over the document; the tools apply the results.
 */
import type { Item, Level, Room, Vec2, Wall } from '../core/hp/types';
import { containsPoint } from '../state/sceneGesture';

export type WallSide = 'north' | 'east' | 'south' | 'west';
export const SIDE_LETTER: Record<WallSide, string> = { north: 'N', east: 'E', south: 'S', west: 'W' };
export const SIDE_FROM_INPUT: Record<string, WallSide> = { north: 'north', n: 'north', top: 'north', север: 'north', с: 'north', east: 'east', e: 'east', right: 'east', восток: 'east', в: 'east', south: 'south', s: 'south', bottom: 'south', юг: 'south', ю: 'south', west: 'west', w: 'west', left: 'west', запад: 'west', з: 'west' };

export interface Size { w: number; d: number; h: number }
export interface Placement { position: Vec2; rotation: number }

/** Yaw whose front points along `direction` (the front is (−sin θ, cos θ)). */
export const rotationFacing = (direction: Vec2): number => Math.atan2(-direction.x, direction.z);
export const frontVector = (rotation: number): Vec2 => ({ x: -Math.sin(rotation), z: Math.cos(rotation) });
/** The item's local +x (its width) on the plan. */
export const rightVector = (rotation: number): Vec2 => ({ x: Math.cos(rotation), z: Math.sin(rotation) });
const add = (a: Vec2, b: Vec2, k = 1): Vec2 => ({ x: a.x + b.x * k, z: a.z + b.z * k });
const round = (p: Vec2): Vec2 => ({ x: Math.round(p.x * 1e4) / 1e4, z: Math.round(p.z * 1e4) / 1e4 });

function onSegment(p: Vec2, a: Vec2, b: Vec2): boolean {
  const dx = b.x - a.x, dz = b.z - a.z, length = Math.hypot(dx, dz) || 1;
  const t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / (length * length);
  if (t < -1e-6 || t > 1 + 1e-6) return false;
  return Math.hypot(a.x + dx * t - p.x, a.z + dz * t - p.z) < 1e-4;
}

/** The walls that bound a room: its own, plus shared walls that lie on its outline. */
export function roomWalls(level: Level, room: Room): Wall[] {
  const edges = room.polygon.map((p, i) => [p, room.polygon[(i + 1) % room.polygon.length]] as const);
  return level.walls.filter(wall => wall.roomIndex === room.index
    || edges.some(([a, b]) => onSegment(wall.start, a, b) && onSegment(wall.end, a, b)));
}

export function roomBounds(room: Room): { minX: number; maxX: number; minZ: number; maxZ: number; centre: Vec2 } {
  const xs = room.polygon.map(p => p.x), zs = room.polygon.map(p => p.z);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minZ = Math.min(...zs), maxZ = Math.max(...zs);
  return { minX, maxX, minZ, maxZ, centre: { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2 } };
}

/** Which side of the room a wall closes: north is the top of the plan (smallest z). */
export function wallSide(room: Room, wall: Wall): WallSide {
  const bounds = roomBounds(room);
  const hw = Math.max((bounds.maxX - bounds.minX) / 2, 1e-6), hd = Math.max((bounds.maxZ - bounds.minZ) / 2, 1e-6);
  const dx = (wall.center.x - bounds.centre.x) / hw, dz = (wall.center.z - bounds.centre.z) / hd;
  return Math.abs(dx) > Math.abs(dz) ? (dx > 0 ? 'east' : 'west') : (dz > 0 ? 'south' : 'north');
}

/** The room's wall on a side; several pieces on one side give the longest. */
export function wallBySide(level: Level, room: Room, side: WallSide): Wall | undefined {
  return roomWalls(level, room).filter(wall => wallSide(room, wall) === side).sort((a, b) => b.length - a.length)[0];
}

/** The unit normal that points from the wall into this room (a shared wall faces two rooms). */
export function inwardNormal(room: Room, wall: Wall): Vec2 {
  const probe = add(wall.center, wall.normalIn, 0.05);
  return containsPoint(room.polygon, probe) ? wall.normalIn : { x: -wall.normalIn.x, z: -wall.normalIn.z };
}

export function openingsOnWall(level: Level, wall: Wall) {
  return level.openings.filter(opening => opening.wallId === wall.id);
}

export interface AlongOptions { from?: 'start' | 'end' | 'center'; offset?: number; gap?: number; elevation?: number }

/**
 * Items in a row against a wall, each with its back to the wall and its front facing the
 * room. `from: end` fills from the wall's end backwards; `center` centres the whole run.
 */
export function alongWall(room: Room, wall: Wall, sizes: readonly Size[], options: AlongOptions = {}): { placements: Placement[]; error?: string } {
  const gap = options.gap ?? 0.02, offset = options.offset ?? 0.05;
  const total = sizes.reduce((sum, size) => sum + size.w, 0) + gap * Math.max(0, sizes.length - 1);
  if (total + (options.from === 'center' ? 0 : offset) > wall.length + 1e-6) return { placements: [], error: `The run is ${total.toFixed(2)} m but wall ${wall.id} is ${wall.length.toFixed(2)} m long.` };
  const n = inwardNormal(room, wall);
  const rotation = rotationFacing(n);
  let cursor = options.from === 'end' ? wall.length - offset : options.from === 'center' ? (wall.length - total) / 2 : offset;
  const direction = options.from === 'end' ? -1 : 1;
  const placements: Placement[] = [];
  for (const size of sizes) {
    const along = cursor + direction * size.w / 2;
    const reach = wall.thickness / 2 + size.d / 2 + 0.005;
    placements.push({ position: round(add(add(wall.start, wall.dir, along), n, reach)), rotation });
    cursor += direction * (size.w + gap);
  }
  return { placements };
}

export type BesideSide = 'left' | 'right' | 'front' | 'behind';

/** A piece next to another: left/right along the anchor's width, front/behind along its depth; in front it turns to face the anchor. */
export function beside(anchor: Pick<Item, 'position' | 'rotation' | 'size'>, size: Size, side: BesideSide, gap = 0.05): Placement {
  const front = frontVector(anchor.rotation), right = rightVector(anchor.rotation);
  const axis = side === 'left' ? { x: -right.x, z: -right.z } : side === 'right' ? right : side === 'front' ? front : { x: -front.x, z: -front.z };
  const half = side === 'left' || side === 'right' ? (anchor.size.w + size.w) / 2 : (anchor.size.d + size.d) / 2;
  return { position: round(add(anchor.position, axis, half + gap)), rotation: side === 'front' ? anchor.rotation + Math.PI : anchor.rotation };
}

/** Chairs around a table, facing it: the long sides fill first (two each when the table is long enough), then the ends, then extra rounds. */
export function around(table: Pick<Item, 'position' | 'rotation' | 'size'>, chair: Size, count: number, gap = 0.05): Placement[] {
  const front = frontVector(table.rotation), right = rightVector(table.rotation);
  const longAlongWidth = table.size.w >= table.size.d;
  const seatsFit = (span: number) => Math.max(1, Math.floor((span + 0.1) / (chair.w + 0.1)));
  // Sides in the order they fill: the two long sides, then the two ends.
  const sides = (longAlongWidth
    ? [{ axis: front, half: table.size.d / 2, spread: right, span: table.size.w }, { axis: { x: -front.x, z: -front.z }, half: table.size.d / 2, spread: right, span: table.size.w },
      { axis: right, half: table.size.w / 2, spread: front, span: table.size.d }, { axis: { x: -right.x, z: -right.z }, half: table.size.w / 2, spread: front, span: table.size.d }]
    : [{ axis: right, half: table.size.w / 2, spread: front, span: table.size.d }, { axis: { x: -right.x, z: -right.z }, half: table.size.w / 2, spread: front, span: table.size.d },
      { axis: front, half: table.size.d / 2, spread: right, span: table.size.w }, { axis: { x: -front.x, z: -front.z }, half: table.size.d / 2, spread: right, span: table.size.w }])
    .map(side => ({ ...side, slots: seatsFit(side.span), seats: 0 }));
  let remaining = count;
  // Long side A, long side B, A again, B again, then the ends — the way a table is actually laid.
  const order = [0, 1, 0, 1, 2, 3];
  for (const index of order) { if (remaining <= 0) break; const side = sides[index]; if (side.seats < side.slots) { side.seats++; remaining--; } }
  for (let round = 0; remaining > 0 && round < 12; round++) for (const side of sides) { if (remaining <= 0) break; side.seats++; remaining--; }
  const placements: Placement[] = [];
  for (const side of sides) {
    const distance = side.half + gap + chair.d / 2;
    for (let seat = 0; seat < side.seats; seat++) {
      const t = side.seats === 1 ? 0 : (seat / (side.seats - 1) - 0.5) * Math.max(0, side.span - chair.w - 0.1);
      const position = round(add(add(table.position, side.axis, distance), side.spread, t));
      placements.push({ position, rotation: rotationFacing({ x: -side.axis.x, z: -side.axis.z }) });
    }
  }
  return placements;
}

/** A point on the room floor some way in from a wall, for a table opposite a kitchen run. */
export function inFromWall(room: Room, wall: Wall, distance: number): Vec2 {
  const n = inwardNormal(room, wall);
  return round(add(wall.center, n, distance));
}
